import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import AlertLine from '@/assets/icons/ri_alert_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import InfoTip from '@/components/atoms/InfoTip';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import { useChartTheme } from '@/components/charts/theme';
import EmptyState from '@/components/molecules/EmptyState';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { alertIconColors } from '@/config/settings';
import type { CageHealthOverview, CageHealthParameter } from '@/domain/cage-health-overview';
import { toHealthPercent } from '@/routes/Client/Cage/pages/Health/_utils/healthDetailOverviewHelpers';
import HealthLaksvelAlertsTooltipContent, {
    type HealthLaksvelAlertItem,
} from '@/routes/Client/Cage/pages/Health/components/HealthLaksvelAlertsTooltipContent/HealthLaksvelAlertsTooltipContent';
import LaksvelScoreLegend from '@/routes/Client/Cage/pages/Health/components/LaksvelScoreLegend/LaksvelScoreLegend';
import {
    laksvelScorePercents,
    ParameterBar,
} from '@/routes/Client/Cage/pages/Health/components/ParameterBar';
import { OVERVIEW_SCORE_LEGEND } from '@/routes/Client/Cage/pages/Health/HealthDashboard/components/LaksvelOverviewChart/laksvelOverviewColors';
import { getHealthMetricUiConfig } from '@/routes/Client/Cage/pages/Health/HealthDashboard/healthMetricConfig';
import { formatDateDisplay } from '@/utils/date';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import {
    CAGE_DASHBOARD_HEALTH_PARAMETER_LIMIT,
    isCageHealthAlertParameter,
    orderCageHealthOverviewParameters,
} from '../../helpers/selectCageHealthOverviewParameters';

import styles from './CageHealthOverviewStatus.module.scss';

interface CageHealthOverviewStatusProps {
    overview: CageHealthOverview | undefined;
    healthUrl: string;
    isLoading?: boolean;
    /** Set when the overview failed to load: the error state with Try again replaces the list. */
    loadError?: RetryProps | null;
}

export default function CageHealthOverviewStatus(props: CageHealthOverviewStatusProps) {
    const { overview, healthUrl, isLoading = false, loadError } = props;
    const { t } = useTranslation('healthModule');
    const { t: tGlobal } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const listId = useId();
    const orderedParameters = orderCageHealthOverviewParameters(overview?.parameters ?? []);
    const visibleParameters = isExpanded
        ? orderedParameters
        : orderedParameters.slice(0, CAGE_DASHBOARD_HEALTH_PARAMETER_LIMIT);
    const canToggleParameters = orderedParameters.length > CAGE_DASHBOARD_HEALTH_PARAMETER_LIMIT;

    const alertItems: HealthLaksvelAlertItem[] = (overview?.parameters ?? [])
        .filter(isCageHealthAlertParameter)
        .map((parameter) => ({
            parameterKey: parameter.key,
            level: parameter.alert.level === 'high' ? 'high' : 'increasing',
        }));

    const lastMeasurementLabel =
        overview?.lastMeasurement != null && overview.lastMeasurement !== ''
            ? formatDateDisplay(overview.lastMeasurement)
            : null;
    const healthOverviewTooltip = t('dashboard.healthOverviewTooltip');

    return (
        <div className={styles.smallSquareBoxContainer}>
            <div className={styles.headerRow}>
                <div className={styles.title}>
                    <h2 className={styles.titleText}>{t('dashboard.healthOverviewTitle')}</h2>
                    <InfoTip
                        topic={t('dashboard.healthOverviewTitle')}
                        content={healthOverviewTooltip}
                    />
                </div>
                <Button
                    variant="secondary"
                    trackingId="cage_dashboard_health_view"
                    to={healthUrl}
                    iconEnd={
                        <SvgIcon
                            color="currentColor"
                            icon={ArrowRightLine}
                            size="custom"
                            customSize={20}
                        />
                    }>
                    {tGlobal('common.actions.view')}
                </Button>
            </div>

            <div className={styles.subtitle}>
                <span>{t('dashboard.lastMeasurement')}</span>
                {isLoading ? (
                    <SkeletonBox
                        width={150}
                        height={24}
                        borderRadius={8}
                        className={styles.skeleton}
                    />
                ) : (
                    <span>{lastMeasurementLabel ?? t('dashboard.notAvailable')}</span>
                )}
            </div>

            {isLoading ? (
                <div className={styles.skeletonList}>
                    {['a', 'b', 'c', 'd'].map((key) => (
                        <SkeletonBox key={key} width="100%" height={36} borderRadius={4} />
                    ))}
                </div>
            ) : loadError ? (
                <SomethingHappened title={t('dashboard.loadError')} {...loadError} />
            ) : visibleParameters.length === 0 ? (
                <EmptyState title={t('dashboard.emptyMetrics')} appearance="quiet" />
            ) : (
                <>
                    <div id={listId} className={styles.list}>
                        {visibleParameters.map((parameter) => (
                            <ParameterBarRow
                                key={parameter.key}
                                parameter={parameter}
                                label={tGlobal(getHealthMetricUiConfig(parameter.key).labelKey)}
                                alertItems={alertItems}
                            />
                        ))}
                    </div>
                    {canToggleParameters ? (
                        <button
                            type="button"
                            className={styles.showMoreButton}
                            aria-expanded={isExpanded}
                            aria-controls={listId}
                            onClick={() => setIsExpanded((current) => !current)}>
                            {isExpanded
                                ? t('dashboard.collapseLaksvelParameters')
                                : t('dashboard.expandLaksvelParameters')}
                        </button>
                    ) : null}
                    <LaksvelScoreLegend className={styles.scoreLegend} />
                </>
            )}
        </div>
    );
}

interface ParameterBarRowProps {
    parameter: CageHealthParameter;
    label: string;
    alertItems: HealthLaksvelAlertItem[];
}

function ParameterBarRow(props: ParameterBarRowProps) {
    const { parameter, label, alertItems } = props;
    const { t, i18n } = useTranslation('healthModule');
    const locale = parseLocale(i18n.language);
    const isAlert = isCageHealthAlertParameter(parameter);
    const chartTheme = useChartTheme();

    const scoreValues = {
        score1: toHealthPercent(parameter.scores.score1) ?? 0,
        score2: toHealthPercent(parameter.scores.score2) ?? 0,
        score3: toHealthPercent(parameter.scores.score3) ?? 0,
    };
    const scorePercents = laksvelScorePercents(
        scoreValues.score1,
        scoreValues.score2,
        scoreValues.score3
    );

    const labelContent = (
        <span className={styles.label}>
            {isAlert ? (
                <SvgIcon
                    icon={AlertLine}
                    className={styles.alertIcon}
                    size="custom"
                    customSize="1.4em"
                    color={alertIconColors.warning}
                    svgProps={{ 'aria-hidden': true }}
                />
            ) : null}
            <span className={styles.labelText}>{label}</span>
        </span>
    );

    const bar = <ParameterBar percents={scorePercents} height="sm" className={styles.bar} />;

    const barTooltipContent = (
        <div className={styles.scoreTooltipBody}>
            {OVERVIEW_SCORE_LEGEND.map((segment) => (
                <div key={segment.key} className={styles.scoreTooltipRow}>
                    <span>{t(segment.labelKey)}</span>
                    <span>{formatNumberByLocale(scorePercents[segment.key], locale, 1)}%</span>
                </div>
            ))}
        </div>
    );

    return (
        <div className={styles.row}>
            {isAlert ? (
                <Tooltip
                    content={<HealthLaksvelAlertsTooltipContent alerts={alertItems} />}
                    maxWidth={320}
                    layout="fit"
                    targetClassName={styles.labelTooltipTarget}>
                    {labelContent}
                </Tooltip>
            ) : (
                <span className={styles.labelSlot}>{labelContent}</span>
            )}
            <Tooltip
                content={barTooltipContent}
                maxWidth={200}
                targetClassName={styles.barTooltipTarget}>
                {bar}
            </Tooltip>
        </div>
    );
}
